import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import { ReferenceMaterialRegistry } from '../registries/ReferenceMaterialRegistry';
import { CustomRendererMode, type EnhancedToolResult } from '../types';

import { ConversationTool } from './ConversationTool';
import type { ToolConstructorArgs } from './Tool';

import type { ToolDefinition, ToolExecutionContext } from 'js/src/agent/types';
import type { VideoPlan, VideoPlanRequest } from 'js/src/client';
import { ApiService, AspectRatio, NarrativeThreadEnum } from 'js/src/client';
import { VideoDraftResultRenderer } from 'js/src/components/video-planner/VideoDraftResultRenderer';
import {
    clearMediaGuidanceOverride,
    selectMediaOptions,
} from 'js/src/data/media-recommendation-preferences';
import { selectSketchControlsBrandKitId } from 'js/src/data/sketch-controls/sketch-controls-selectors';
import { createAndPopulateVideoDraftFromVideoPlan } from 'js/src/data/video-planner';
import { isApiError } from 'js/src/types';

/**
 * Converts a VideoPlan into CreateVideoFromPlanArgs for programmatic tool execution.
 * Used by follow-on action widgets that create videos from existing plans.
 */
export function buildCreateVideoFromPlanArgs(videoPlan: VideoPlan): CreateVideoFromPlanArgs {
    const args: CreateVideoFromPlanArgs = {
        title: videoPlan.title,
        script: videoPlan.script ?? '',
        aspect_ratio: videoPlan.aspect_ratio,
        template_id: videoPlan.template_id,
        narrative_thread: videoPlan.narrative_thread,
    };
    if (videoPlan.video_prompt) args.video_prompt = videoPlan.video_prompt;
    if (videoPlan.ai_voice) args.ai_voice = videoPlan.ai_voice;
    if (videoPlan.ai_avatar) args.ai_avatar = videoPlan.ai_avatar;
    if (videoPlan.duration_target_seconds)
        args.duration_target_seconds = videoPlan.duration_target_seconds;
    if (videoPlan.blueprint_id !== null && videoPlan.blueprint_id !== undefined)
        args.blueprint_id = videoPlan.blueprint_id;
    return args;
}

export const createVideoFromPlanSchema = z.object({
    title: z.string().describe('Title of the video'),
    script: z
        .string()
        .describe(
            'Exact script to use for the video. This will be read or displayed as text to users, so should only contain the actual narration/text content, NOT instructions or guidance.',
        ),
    video_prompt: z.string().optional().describe('Video generation prompt (optional)'),
    aspect_ratio: z.enum(AspectRatio).describe('Aspect ratio for the video'),
    // The allowed template ids are per-instance (the templates available to this agent),
    // so the enum is injected in extendParameters rather than declared here.
    template_id: z.string().describe('Template ID to use for the video'),
    narrative_thread: z.enum(NarrativeThreadEnum).describe('Narrative thread type'),
    ai_voice: z
        .string()
        .optional()
        .describe(
            'AI voice slug to use for voiceover videos (optional, only valid when narrative_thread is "voiceover", make sure to pick a specified voice ID and not make one up)',
        ),
    ai_avatar: z.number().optional().describe('AI avatar ID'),
    duration_target_seconds: z
        .number()
        .min(10)
        .optional()
        .describe('Target duration in seconds (optional)'),
    blueprint_id: z
        .number()
        .optional()
        .describe(
            "Blueprint ID to use to create a video matching the specified blueprint's style, pacing, and structure (optional)",
        ),
});
export type CreateVideoFromPlanArgs = z.infer<typeof createVideoFromPlanSchema>;

interface CreateVideoFromPlanToolConstructorArgs extends ToolConstructorArgs {
    templateIds?: string[];
}

export class CreateVideoFromPlanTool extends ConversationTool<
    typeof createVideoFromPlanSchema,
    CreateVideoFromPlanToolConstructorArgs
> {
    public static readonly toolName = 'create_video_from_plan';

    protected override readonly schema = createVideoFromPlanSchema;
    protected override readonly toolName = CreateVideoFromPlanTool.toolName;
    protected override readonly toolDescription =
        'Create a video using an exact script and settings.';

    public override isLLMCallable = false;
    public override canRunAsync = false;
    public override customRendererMode = CustomRendererMode.INLINE;
    override customRenderer = VideoDraftResultRenderer;
    private templateIds: string[];

    public constructor(args: CreateVideoFromPlanToolConstructorArgs) {
        super(args);
        this.templateIds = args.templateIds || [];
    }

    // template_id's allowed values are the templates available to this agent instance,
    // which the module-level schema can't see. Inject them as the enum the model chooses from.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        if (this.templateIds.length > 0) {
            (parameters.properties.template_id as { enum?: string[] }).enum = this.templateIds;
        }
        return parameters;
    }

    getToolDisplayName(): string {
        return 'Creating video';
    }

    public get statusIcon(): string {
        return 'movie';
    }

    async execute(
        args: CreateVideoFromPlanArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        try {
            const videoDraftId = context.toolCallId;

            // Get reference material from registry
            const referenceMaterialRegistry = ReferenceMaterialRegistry.getInstance();
            const referenceMaterial = referenceMaterialRegistry.getContent();

            // Get source media UUIDs from MediaRegistry
            const mediaRegistry = MediaRegistry.getInstance();
            const sourceMediaUUIDs = mediaRegistry.getSourceMediaUuids();
            // Read media options and clear the transient override so it doesn't leak
            // to subsequent video creations (set by media concepts follow-on action)
            const mediaOptions = selectMediaOptions(this._getState());
            this._dispatch(clearMediaGuidanceOverride());

            // Append media guidance to video_prompt if set
            const mediaGuidance = mediaOptions.media_guidance?.trim();
            let videoPrompt = args.video_prompt ?? null;
            if (mediaGuidance) {
                videoPrompt = videoPrompt
                    ? `${videoPrompt}\n\nMedia guidance: ${mediaGuidance}`
                    : `Media guidance: ${mediaGuidance}`;
            }

            const brandKitId = selectSketchControlsBrandKitId(this._getState());

            const planRequest: VideoPlanRequest = {
                title: args.title,
                script: args.script,
                video_prompt: videoPrompt,
                aspect_ratio: args.aspect_ratio,
                template_id: args.template_id,
                narrative_thread: args.narrative_thread,
                ai_voice: args.ai_voice ?? null,
                ai_avatar: args.ai_avatar ?? null,
                duration_target_seconds: args.duration_target_seconds ?? null,
                blueprint_id: args.blueprint_id ?? null,
            };

            // Validate through the backend so missing ai_voice/ai_avatar (and other
            // plan fields) are filled in with defaults appropriate for the team.
            const validationResponse = await ApiService.sketchValidateVideoPlansCreate({
                body: { plans: [planRequest] },
            });
            if (!validationResponse.data.valid) {
                return {
                    result: `Video plan validation failed: ${validationResponse.data.errors.join(', ')}`,
                    isSuccess: false,
                };
            }

            const validatedPlans = validationResponse.data.validated_plans;
            if (!validatedPlans || validatedPlans.length === 0) {
                return {
                    result: 'No validated plans returned from server',
                    isSuccess: false,
                };
            }
            const validatedPlan = validatedPlans[0];

            const videoPlan = { ...validatedPlan, id: videoDraftId };

            const result = await this._dispatch(
                createAndPopulateVideoDraftFromVideoPlan({
                    videoDraftId,
                    videoPlan,
                    referenceMaterial,
                    sourceMediaUUIDs: sourceMediaUUIDs.length > 0 ? sourceMediaUUIDs : undefined,
                    creationToolName: CreateVideoFromPlanTool.toolName,
                    mediaOptions,
                    conversationId: context.conversationId,
                    brandKitId,
                }),
            );

            if (!result.success) {
                return {
                    result: result.errorMessage ?? 'Video draft generation failed.',
                    isSuccess: false,
                };
            }

            if (this._parentConversation) {
                this._parentConversation.setArtifactTaskIdForToolCall(
                    context.toolCallId,
                    result.taskId,
                );
            }

            analytics.track('Sketch: Video Created From Plan', {
                plan_title: args.title,
                template_id: args.template_id,
                aspect_ratio: args.aspect_ratio,
                narrative_thread: args.narrative_thread,
                ai_voice: args.ai_voice,
                duration_target_seconds: args.duration_target_seconds,
                blueprint_id: args.blueprint_id,
                brand_kit_id: brandKitId,
            });

            return {
                result: JSON.stringify({
                    videoDraftId,
                    success:
                        "Created video draft. Video is embedded above and will load shortly. The user can click it once it's finished loading to view, edit and download.",
                }),
                isSuccess: true,
            };
        } catch (error) {
            let errorMessage = 'Unknown error occurred';
            if (isApiError(error)) {
                errorMessage = `${error.message}: ${JSON.stringify(error.body)}`;
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }
            return {
                result: errorMessage,
                isSuccess: false,
            };
        }
    }
}
